import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Divider,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS from "../../../constants/apiConstants";
import { ADMIN_SCHOOL_EDIT_ROUTE } from "../../../routes/RoutesPathes";
import useAuth from "../../../hooks/useAuth";
import SchoolStorage from "../../../utils/localStorage";

const initialFormState = {
  name: "",
  description: "",
  capacity: 0,
  phone: "",
  country: "",
  programType: "",
};

export const AddNewSchool = () => {
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const fileInputRef = useRef(null);
  const { setSchool, setSchools } = useAuth();

  const [form, setForm] = useState(initialFormState);
  const [countries, setCountries] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [logoPreview, setLogoPreview] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoadingOptions(true);
        const [programRes, countriesRes] = await Promise.all([
          axiosPrivate.get(API_URLS.programType),
          axiosPrivate.get(API_URLS.country),
        ]);

        const programRows = Array.isArray(programRes.data)
          ? programRes.data
          : programRes.data?.results || [];
        const countryRows = Array.isArray(countriesRes.data)
          ? countriesRes.data
          : countriesRes.data?.results || [];

        setCountries(
          countryRows.map((country) => ({
            id: country.code || country.Code,
            name: country.name || country.Name,
          }))
        );
        setProgramTypes(
          programRows.map((program) => ({
            id: program.id,
            name: program.Name || program.Name_en || program.Name_ar,
          }))
        );
      } catch (error) {
        console.error("Failed to load school options", error);
        setErrorMessage("Failed to load form options.");
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, [axiosPrivate]);

  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoPreview(URL.createObjectURL(file));
    setLogoFile(file);
  };

  const validateForm = () => {
    const errors = {};
    const phoneRegex = /^\+?\d{7,15}$/;

    if (!form.name.trim()) {
      errors.name = "School name is required.";
    }

    if (!form.capacity || !Number.isInteger(Number(form.capacity)) || Number(form.capacity) <= 0) {
      errors.capacity = "Capacity must be a positive integer.";
    }

    if (!form.country) {
      errors.country = "Country is required.";
    }

    if (!form.programType) {
      errors.programType = "Program type is required.";
    }

    if (!form.phone) {
      errors.phone = "Phone number is required.";
    } else if (!phoneRegex.test(form.phone)) {
      errors.phone = "Enter a valid phone number.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSubmitMessage("");

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = new FormData();
      payload.append("Name", form.name.trim());
      payload.append("Description", form.description.trim());
      if (form.capacity) payload.append("Capacity", form.capacity);
      if (form.phone) payload.append("Phone", form.phone);
      if (form.country) payload.append("countries", form.country);
      if (form.programType) payload.append("ProgramType", form.programType);
      if (logoFile) payload.append("LogoImage", logoFile);

      const response = await axiosPrivate.post(API_URLS.createSchool(), payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const createdSchool = response?.data?.nursery || response?.data || {};
      const newSchoolOption = {
        value: createdSchool.id,
        label: createdSchool.Name || form.name.trim(),
      };

      if (newSchoolOption.value) {
        setSchool({ id: newSchoolOption.value, name: newSchoolOption.label });
        SchoolStorage.setSchool(newSchoolOption.value, newSchoolOption.label);
        setSchools((prev) => {
          const existing = prev?.some((school) => school.value === newSchoolOption.value);
          const nextSchools = existing ? prev : [...(prev || []), newSchoolOption];
          window.dispatchEvent(
            new CustomEvent("schools:updated", { detail: { schools: nextSchools } })
          );
          return nextSchools;
        });
      }

      setSubmitMessage("School created successfully.");
      setForm(initialFormState);
      setLogoFile(null);
      setLogoPreview("");
      navigate(ADMIN_SCHOOL_EDIT_ROUTE);
      window.location.reload();
    } catch (error) {
      console.error("Create school failed", error);
      setErrorMessage("Failed to create school. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ px: 4, py: 5 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" fontWeight="bold">
          Add New School
        </Typography>
        <Button variant="outlined" onClick={() => navigate(ADMIN_SCHOOL_EDIT_ROUTE)}>
          Back to School Management
        </Button>
      </Box>

      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          School Information
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Add a new school or nursery to manage under the same owner account.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <form onSubmit={handleSubmit}>
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={3}>
            <TextField
              label="School Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              fullWidth
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
            />
            <TextField
              label="Phone Number"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              type="tel"
              fullWidth
              error={!!fieldErrors.phone}
              helperText={fieldErrors.phone}
            />
            <TextField
              label="Capacity"
              name="capacity"
              type="number"
              value={form.capacity}
              onChange={handleChange}
              fullWidth
              inputProps={{ min: 1, step: 1 }}
              error={!!fieldErrors.capacity}
              helperText={fieldErrors.capacity}
            />
            <TextField
              label="Country"
              name="country"
              select
              value={form.country}
              onChange={handleChange}
              fullWidth
              disabled={loadingOptions}
              error={!!fieldErrors.country}
              helperText={fieldErrors.country}
            >
              {countries.map((country) => (
                <MenuItem key={country.id} value={country.id}>
                  {country.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Program Type"
              name="programType"
              select
              value={form.programType}
              onChange={handleChange}
              fullWidth
              disabled={loadingOptions}
              error={!!fieldErrors.programType}
              helperText={fieldErrors.programType}
            >
              {programTypes.map((program) => (
                <MenuItem key={program.id} value={program.id}>
                  {program.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              name="description"
              value={form.description}
              onChange={handleChange}
              multiline
              minRows={3}
              fullWidth
            />
          </Box>

          <Box
            display="flex"
            alignItems="center"
            gap={2}
            sx={{ mt: 4 }}
          >
            <Avatar
              src={logoPreview}
              sx={{ width: 70, height: 70 }}
              variant="rounded"
            />
            <div>
              <Typography variant="subtitle1" fontWeight="bold">
                School Logo
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload a logo to help identify this school.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload Logo
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                style={{ display: "none" }}
              />
            </div>
          </Box>

          {errorMessage && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {errorMessage}
            </Alert>
          )}
          {submitMessage && (
            <Alert severity="info" sx={{ mt: 3 }}>
              {submitMessage}
            </Alert>
          )}

          <Box display="flex" gap={2} sx={{ mt: 4 }}>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create School"}
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setForm(initialFormState);
                setLogoFile(null);
                setLogoPreview("");
              }}
            >
              Reset
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};
